import { PricingDiscount } from '../api/asset/assetApiTypes';

export type PricingValidationError = { key: string };

/**
 * Safety-net validator for the wire-shape PricingDiscount[] sent to the
 * backend. The split-point UI in PricingSection makes these invariants
 * structural, but we still check before save to catch any bad state.
 *
 * Rules:
 * - The first tier must cover night 1 (no gap before the first discount).
 * - The last tier must be open-ended (to === -1); non-last tiers must not be.
 * - Tiers must be strictly sequential: each tier's from equals the previous
 *   tier's to + 1, every from >= 1, and non-last tier to >= from.
 * - Each discount percentage must be in [0, 100].
 */
/**
 * Every base price is a usable amount: finite and non-negative. An explicit 0
 * is valid for every room use case — a free room (internal meeting rooms,
 * aggregate billing elsewhere) is a real configuration. The forgotten-price
 * danger is handled at the INPUT instead: PricingSection maps an emptied
 * field to NaN, never to 0, so "deliberately free" and "price forgotten"
 * cannot be confused — NaN fails this check and blocks the save.
 *
 * Shared by AssetCreatePage and AssetDetailPage — one rule, one place.
 */
export function priceScaleAmountsValid(
    scales: { price: number }[],
): boolean {
    return scales.every((s) => Number.isFinite(s.price) && s.price >= 0);
}

export function validatePricingDiscounts(
    discounts: PricingDiscount[],
): PricingValidationError | null {
    if (!discounts || discounts.length === 0) return null;

    if (discounts[0].nights[0] !== 1) {
        return { key: 'asset_detail.validation.discount_first_tier_from_one' };
    }

    const lastIndex = discounts.length - 1;

    for (let i = 0; i < discounts.length; i++) {
        const [from, to] = discounts[i].nights;
        const isLast = i === lastIndex;

        if (isLast) {
            if (to !== -1) return { key: 'asset_detail.validation.discount_last_open_ended' };
        } else {
            if (to === -1 || to < from) {
                return { key: 'asset_detail.validation.discount_sequential' };
            }
        }

        if (from < 1) return { key: 'asset_detail.validation.discount_sequential' };

        if (i > 0) {
            const prevTo = discounts[i - 1].nights[1];
            if (from !== prevTo + 1) {
                return { key: 'asset_detail.validation.discount_sequential' };
            }
        }

        const pct = discounts[i].discount;
        if (!Number.isFinite(pct) || pct < 0 || pct > 100) {
            return { key: 'asset_detail.validation.discount_percentage_range' };
        }
    }

    return null;
}
